import { useQuery } from '@tanstack/react-query';
import { api, type QueryParams } from '@/lib/api';
import type {
  MaintenanceDueResponse,
  MaintenanceDueStatus,
  MaintenanceFrequency,
} from '@/types/api';

/**
 * Hook für die Fälligkeitsliste (welche Kunden gehören gewartet).
 *
 * Backend-Endpunkte (Source of Truth):
 *   GET /maintenance/due?within_days=&include_ok=
 *     -> { data: MaintenanceDueRow[], meta: MaintenanceDueMeta }
 *
 * Semantik (app/api/routes/maintenance.py):
 *   - within_days: Horizont für "due_soon" (Default 14, 0..365).
 *   - include_ok=false (Default): nur overdue + due_soon in `data`;
 *     die meta-Zähler laufen IMMER über alle Kunden.
 *   - on_demand-Kunden und Kunden ohne in-scope-Systeme fehlen in `data`
 *     und tauchen nur in meta.on_demand_excluded / no_systems_excluded auf.
 */

export interface MaintenanceDueOptions {
  /** Horizont in Tagen (Default 14). */
  withinDays?: number;
  /** true = auch nicht-faellige Kunden ("ok") zurückgeben. */
  includeOk?: boolean;
}

// --- Deutsche Labels (zentral, damit Dashboard/Liste identisch beschriften) --

export const DUE_STATUS_LABELS: Record<MaintenanceDueStatus, string> = {
  overdue: 'Überfällig',
  due_soon: 'Bald fällig',
  ok: 'OK',
};

export const FREQUENCY_LABELS: Record<MaintenanceFrequency, string> = {
  monthly: 'Monatlich',
  quarterly: 'Quartalsweise',
  biannual: 'Halbjährlich',
  yearly: 'Jährlich',
  on_demand: 'Auf Abruf',
  custom: 'Individuell',
};

// --- Query-Keys --------------------------------------------------------------

export const maintenanceDueKeys = {
  all: ['maintenance-due'] as const,
  list: (withinDays: number, includeOk: boolean) =>
    ['maintenance-due', withinDays, includeOk] as const,
};

// --- Query -------------------------------------------------------------------

export function useMaintenanceDue(
  opts: MaintenanceDueOptions = {}
): ReturnType<typeof useQuery<MaintenanceDueResponse>> {
  const withinDays = opts.withinDays ?? 14;
  const includeOk = opts.includeOk ?? false;

  const params: QueryParams = { within_days: withinDays };
  // buildQuery lässt false-Werte weg - explizit nur bei true mitschicken
  // (Backend-Default ist ohnehin false).
  if (includeOk) params.include_ok = true;

  return useQuery<MaintenanceDueResponse>({
    queryKey: maintenanceDueKeys.list(withinDays, includeOk),
    queryFn: () => api.get<MaintenanceDueResponse>('/maintenance/due', params),
    staleTime: 30_000,
  });
}
